/** tpl 31 — `{affected_fields: [str, …]}`; empty = the backend's default
 *  field set (names, email, start date/time, pickup, pax count). */
import AddIcon from '@mui/icons-material/Add';
import { Chip, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import type { ArgEditorProps } from './common';
import { argsOf, stringList } from './common';

export function AffectedFieldsArgs({ rule, onArgsChange, disabled }: ArgEditorProps) {
  const args = argsOf(rule);
  const fields = stringList(args.affected_fields);
  const [input, setInput] = useState('');

  const add = () => {
    const name = input.trim();
    if (!name || fields.includes(name)) return;
    onArgsChange({ ...args, affected_fields: [...fields, name] });
    setInput('');
  };

  return (
    <Stack spacing={1}>
      <TextField
        size="small"
        label="Add field"
        sx={{ maxWidth: 360 }}
        value={input}
        disabled={disabled}
        helperText='Dotted context paths, "*" wildcards allowed (e.g. "bookings.*.pickup")'
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          }
          if (e.key === 'Escape') setInput('');
        }}
        InputProps={{
          endAdornment: (
            <IconButton size="small" aria-label="Add field" onClick={add} disabled={disabled || !input.trim()}>
              <AddIcon fontSize="small" />
            </IconButton>
          ),
        }}
      />
      {fields.length ? (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {fields.map((f) => (
            <Chip
              key={f}
              size="small"
              label={f}
              sx={{ fontFamily: 'monospace' }}
              {...(disabled
                ? {}
                : { onDelete: () => onArgsChange({ ...args, affected_fields: fields.filter((x) => x !== f) }) })}
            />
          ))}
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          No fields listed — the default set applies (names, email, start date/time, pickup, passenger count).
        </Typography>
      )}
    </Stack>
  );
}
